// /////////////////////////////////////////////////////////////
// [ファイル] useSourceQuality.ts
// [概要] ソース品質管理プラットフォーム フェーズ1 (MVP) のデータフック。
//        現状はサンプルデータをローカル state に保持し、メタ情報 (状態 /
//        担当 / タグ / 課題) の CRUD をクライアント内で完結させる。
//        バックエンド配線後は、初期データ取得を実 API に、各 CRUD を
//        書き込み API 呼び出しに差し替える (戻り値インターフェースは維持)。
// /////////////////////////////////////////////////////////////
import { useCallback, useMemo, useState } from 'react';
import {
  SourceModule,
  SourceSymbol,
  SourceIssue,
  ReviewStatus,
  IssueSeverity,
} from '../types/sourceQuality';
import { SOURCE_QUALITY_SAMPLE } from '../data/sourceQualitySample';

/** サンプルデータの深いコピー (state の初期値が共有参照にならないように)。 */
const cloneSample = (): SourceModule[] =>
  JSON.parse(JSON.stringify(SOURCE_QUALITY_SAMPLE)) as SourceModule[];

const newId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `id-${Math.random().toString(36).slice(2)}`;

export interface NewIssueInput {
  title: string;
  body?: string;
  severity: IssueSeverity;
}

export function useSourceQuality() {
  const [modules, setModules] = useState<SourceModule[]>(cloneSample);
  const [selectedSymbolId, setSelectedSymbolId] = useState<string | null>(
    SOURCE_QUALITY_SAMPLE[0]?.symbols[0]?.symbol_id ?? null
  );

  const selectedSymbol = useMemo<SourceSymbol | null>(() => {
    if (!selectedSymbolId) return null;
    for (const m of modules) {
      const found = m.symbols.find((s) => s.symbol_id === selectedSymbolId);
      if (found) return found;
    }
    return null;
  }, [modules, selectedSymbolId]);

  /** 対象シンボルを 1 件だけ更新する共通ヘルパ。 */
  const updateSymbol = useCallback(
    (symbolId: string, updater: (s: SourceSymbol) => SourceSymbol) => {
      setModules((prev) =>
        prev.map((m) => ({
          ...m,
          symbols: m.symbols.map((s) => (s.symbol_id === symbolId ? updater(s) : s)),
        }))
      );
    },
    []
  );

  const setReviewStatus = useCallback(
    (symbolId: string, review_status: ReviewStatus) =>
      updateSymbol(symbolId, (s) => ({ ...s, review_status })),
    [updateSymbol]
  );

  const setAssignee = useCallback(
    (symbolId: string, assignee: string | null) =>
      updateSymbol(symbolId, (s) => ({ ...s, assignee })),
    [updateSymbol]
  );

  const addTag = useCallback(
    (symbolId: string, tag: string) => {
      const trimmed = tag.trim();
      if (!trimmed) return;
      updateSymbol(symbolId, (s) =>
        s.tags.includes(trimmed) ? s : { ...s, tags: [...s.tags, trimmed] }
      );
    },
    [updateSymbol]
  );

  const removeTag = useCallback(
    (symbolId: string, tag: string) =>
      updateSymbol(symbolId, (s) => ({ ...s, tags: s.tags.filter((t) => t !== tag) })),
    [updateSymbol]
  );

  const addIssue = useCallback(
    (symbolId: string, input: NewIssueInput) => {
      const issue: SourceIssue = {
        id: newId(),
        title: input.title.trim(),
        body: input.body?.trim() || undefined,
        severity: input.severity,
        status: 'open',
        created_at: new Date().toISOString().slice(0, 10),
      };
      updateSymbol(symbolId, (s) => ({ ...s, issues: [...s.issues, issue] }));
    },
    [updateSymbol]
  );

  const removeIssue = useCallback(
    (symbolId: string, issueId: string) =>
      updateSymbol(symbolId, (s) => ({
        ...s,
        issues: s.issues.filter((i) => i.id !== issueId),
      })),
    [updateSymbol]
  );

  const toggleIssueStatus = useCallback(
    (symbolId: string, issueId: string) =>
      updateSymbol(symbolId, (s) => ({
        ...s,
        issues: s.issues.map((i) =>
          i.id === issueId
            ? { ...i, status: i.status === 'open' ? 'resolved' : 'open' }
            : i
        ),
      })),
    [updateSymbol]
  );

  return {
    modules,
    selectedSymbolId,
    setSelectedSymbolId,
    selectedSymbol,
    setReviewStatus,
    setAssignee,
    addTag,
    removeTag,
    addIssue,
    removeIssue,
    toggleIssueStatus,
  };
}
